import type { ShaderDataTypeKeys } from "models-webgl2";
import { createShader, createProgram } from "../utils";
import { Webgl2 } from "../webgl2";
import {
  ShaderUniformBlock,
  ShaderUniforms,
  ShaderUniformInfo,
  ShaderUniformShadersKeys,
  MapShaderUniformsShaders,
} from "./shader.types";

export class Shader<V extends string = string, F extends string = string> {
  private readonly _gl: WebGL2RenderingContext;

  public readonly name: string;
  public readonly program: WebGLProgram;

  public uniformBlocks: ShaderUniformBlock[];
  public uniforms: ShaderUniforms<ShaderUniformShadersKeys<V, F>>;

  constructor(name: string, vertSrc: V, fragScr: F) {
    this._gl = Webgl2.gl;

    this.name = name;

    // create GLSL shaders, upload the GLSL source, compile the shaders
    const vertexShader = createShader(this._gl, vertSrc, "vertex") as WebGLShader;
    const fragmentShader = createShader(this._gl, fragScr, "fragment") as WebGLShader;

    // Link the two shaders into a program
    this.program = createProgram(this._gl, [vertexShader, fragmentShader]) as WebGLProgram;
    this.uniforms = this._extractUniforms(vertSrc, fragScr);
    this.uniformBlocks = this._extractUniformBlocks(vertSrc, fragScr);
  }

  public getAttribLocation(name: string) {
    return this._gl.getAttribLocation(this.program, name);
  }

  public getUniformLocation(name: string): WebGLUniformLocation {
    return this._gl.getUniformLocation(this.program, name) as WebGLUniformLocation;
  }

  public setUniforms(uniforms: MapShaderUniformsShaders<V, F>) {
    for (const key in this.uniforms) {
      const value = uniforms[key];
      if (value == null) continue;
      this.uniforms[key].setter(value);
    }
  }

  public bind() {
    this._gl.useProgram(this.program);
  }

  public unbind() {
    this._gl.useProgram(null);
  }

  public destroy() {
    this._gl.deleteProgram(this.program);
  }

  private _extractUniforms(
    vertSrc: string,
    fragSrc: string,
  ): ShaderUniforms<ShaderUniformShadersKeys<V, F>> {
    const vertUniforms = vertSrc.match(/uniform \w+ \w+(\[\d+\])?/g) || [];
    const fragUniforms = fragSrc.match(/uniform \w+ \w+(\[\d+\])?/g) || [];

    const uniforms = Array.from(new Set([...vertUniforms, ...fragUniforms]));
    let textureUnit = 0;

    return uniforms.reduce<ShaderUniforms<ShaderUniformShadersKeys<V, F>>>((acc, item) => {
      const [, type, uniformName] = item.split(" ") as [any, ShaderDataTypeKeys, string];
      const containsArray = !!uniformName.match(/\[\d+\]/);
      const name = uniformName.replace(/\[\d+\]/, "");

      const location = this.getUniformLocation(name);

      const callback = this._getUniformSetterByType({ type, containsArray }, location);
      let setter = callback;

      if (type === "sampler2D") {
        const unit = textureUnit;

        setter = (value: any) => {
          callback(unit);
          this._gl.activeTexture(this._gl.TEXTURE0 + unit);
          this._gl.bindTexture(this._gl.TEXTURE_2D, value);
        };

        textureUnit++;
      }

      return { ...acc, [name]: { type, location, setter, containsArray } };
    }, {} as any) as any;
  }

  private _extractUniformBlocks(vertSrc: string, fragSrc: string) {
    const vertUniformBlocks = vertSrc.match(/layout \(std140\) uniform \w+/g) || [];
    const fragUniformBlocks = fragSrc.match(/layout \(std140\) uniform \w+/g) || [];

    const uniformBlocks = Array.from(new Set([...vertUniformBlocks, ...fragUniformBlocks]));

    return uniformBlocks.map((item, i) => {
      const name = item.split(" ").pop() as string;
      const index = this._gl.getUniformBlockIndex(this.program, name);
      this._gl.uniformBlockBinding(this.program, index, i);

      return {
        name,
        index,
        target: i,
      };
    });
  }

  private _getUniformSetterByType(
    uniformInfo: Pick<ShaderUniformInfo, "type" | "containsArray">,
    location: WebGLUniformLocation,
  ) {
    switch (uniformInfo.type) {
      case "float":
        return (value: any) =>
          uniformInfo.containsArray
            ? this._gl.uniform1fv(location, value)
            : this._gl.uniform1f(location, value);
      case "vec2":
        return (value: any) => this._gl.uniform2fv(location, value);
      case "vec3":
        return (value: any) => this._gl.uniform3fv(location, value);
      case "vec4":
        return (value: any) => this._gl.uniform4fv(location, value);
      case "mat3":
        return (value: any) => this._gl.uniformMatrix3fv(location, false, value);
      case "mat4":
        return (value: any) => this._gl.uniformMatrix4fv(location, false, value);
      case "sampler2D":
      case "bool":
      case "uint":
      case "int":
        return (value: any) => this._gl.uniform1i(location, value);
      case "ivec2":
        return (value: any) => this._gl.uniform2iv(location, value);
      case "ivec3":
        return (value: any) => this._gl.uniform3iv(location, value);
      case "ivec4":
        return (value: any) => this._gl.uniform4iv(location, value);
    }
  }
}

export default Shader;
